import React, { ErrorInfo } from 'react';
import { TFunction } from 'i18next';
import { InfoField } from '@components-clean/core/info-field';
import { useTranslation } from '@hooks/index';
import Log from '@utils/logger';

type State = {
    hasError: boolean;
};

type Props = {
    t: TFunction<'translation', undefined>;
} & React.PropsWithChildren;

function WithTranslations(Component: React.ComponentType<Props>) {
    return function WrappedComponent(props: Record<string, unknown>) {
        const { t } = useTranslation();
        return <Component {...props} t={t} />;
    };
}

class PanelErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        Log.error({ error, errorInfo });
    }

    render() {
        const { hasError } = this.state;
        const { children, t } = this.props;
        if (hasError) {
            return <InfoField type="Error" layout="center" message={t('cm:componentsClean.panels.panelError')} />;
        }
        return children;
    }
}

export default WithTranslations(PanelErrorBoundary);
